import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import {
  MAX_RISK_SCORE,
  MIN_RISK_SCORE,
  getRiskBandGaps,
  toRiskBandRows,
  validateRiskBands,
} from 'utils/riskBands';

/**
 * Editor for a set of risk score bands, controlled by its parent.
 *
 * Used twice with the same rows and the same rules, against two different
 * write paths: the platform default on the global config, and one org's
 * override on the organization itself.
 *
 * Rows are kept as strings so a half typed number ("8" on the way to "80")
 * doesn't get coerced out from under the cursor -- `toRiskBandPayload` turns
 * them into numbers on save.
 *
 * What "reset to default" means differs by caller, so it is passed in:
 * resetting an org goes back to the platform default it would otherwise
 * inherit, while resetting the platform default itself goes back to the
 * hardcoded fallback (`defaultBands` left off).
 */
const RiskBandsEditor = ({
  rows,
  onChange,
  disabled,
  defaultBands,
  defaultLabel,
}) => {
  const error = validateRiskBands(rows);
  const gaps = error ? [] : getRiskBandGaps(rows);

  const updateRow = (index, changes) =>
    onChange(
      rows.map((row, i) => (i === index ? { ...row, ...changes } : row)),
    );

  const addRow = () => {
    // A new band starts where the last one ended, which is the shape that
    // validates -- bands touch at their boundaries.
    const last = rows[rows.length - 1];
    const from = last?.max ?? String(MIN_RISK_SCORE);
    onChange([
      ...rows,
      { tag: '', min: String(from), max: String(MAX_RISK_SCORE) },
    ]);
  };

  const removeRow = (index) => onChange(rows.filter((_, i) => i !== index));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {rows.map((row, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            label="Label"
            size="small"
            disabled={disabled}
            value={row?.tag || ''}
            placeholder="High Risk"
            sx={{ flex: 1, minWidth: 160 }}
            onChange={(e) => updateRow(index, { tag: e.target.value })}
          />
          <TextField
            label="From"
            size="small"
            type="number"
            disabled={disabled}
            value={row?.min ?? ''}
            sx={{ width: 110 }}
            inputProps={{ min: MIN_RISK_SCORE, max: MAX_RISK_SCORE }}
            onChange={(e) => updateRow(index, { min: e.target.value })}
          />
          <TextField
            label="To"
            size="small"
            type="number"
            disabled={disabled}
            value={row?.max ?? ''}
            sx={{ width: 110 }}
            inputProps={{ min: MIN_RISK_SCORE, max: MAX_RISK_SCORE }}
            onChange={(e) => updateRow(index, { max: e.target.value })}
          />
          <Tooltip arrow placement="top" title="Remove band">
            <span>
              <IconButton
                size="small"
                disabled={disabled || rows.length < 2}
                onClick={() => removeRow(index)}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ))}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small"
          disabled={disabled}
          onClick={() => onChange(toRiskBandRows(defaultBands))}
        >
          {defaultLabel}
        </Button>
      </Box>

      <Typography variant="caption" color="text.secondary">
        Scores run {MIN_RISK_SCORE} - {MAX_RISK_SCORE}. Bands may touch at a
        boundary but must not overlap; a score on a boundary is classified as
        the lower band.
      </Typography>
      {error ? (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      ) : null}
      {gaps.length ? (
        <Typography variant="caption" color="warning.main">
          {/* Allowed by the API, but a score in the gap comes back with no tag
              at all, which reads as a bug in the report. */}
          Scores in {gaps.map(([from, to]) => `${from} - ${to}`).join(', ')}{' '}
          fall outside every band and will get no label.
        </Typography>
      ) : null}
    </Box>
  );
};

RiskBandsEditor.propTypes = {
  /** `{ tag, min, max }` rows, values held as strings. */
  rows: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  /** What the reset button goes back to; the hardcoded fallback when unset. */
  defaultBands: PropTypes.array,
  defaultLabel: PropTypes.string,
};

RiskBandsEditor.defaultProps = {
  defaultLabel: 'Reset to Default',
};

export default RiskBandsEditor;
